import React, { useEffect, useMemo, useState } from "react";
import { Building, Search, Eye, RefreshCw } from "lucide-react";
import LoadingSpinner from "../../common/LoadingSpinner";
import { projectsAPI } from "../../../services/api";

const normalizeStatus = (status) => String(status || "").toLowerCase().trim();

const getStatusColor = (status) => {
  const s = normalizeStatus(status);
  if (s === "active") return "bg-green-100 text-green-800";
  if (s === "completed") return "bg-blue-100 text-blue-800";
  if (s === "on_hold" || s === "on-hold") return "bg-yellow-100 text-yellow-800";
  if (s === "cancelled" || s === "canceled") return "bg-red-100 text-red-800";
  return "bg-gray-100 text-gray-800";
};

const MyProjectsView = ({ onSelectProject }) => {
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const filteredProjects = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const wantedStatus = normalizeStatus(statusFilter);

    return (projects || []).filter((p) => {
      if (q) {
        const hay = `${p.name || ""} ${p.client || ""} ${p.project_number || ""} ${p.location || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }

      if (wantedStatus !== "all") {
        const pStatus = normalizeStatus(p.status);
        if (wantedStatus === "on-hold" && pStatus === "on_hold") return true;
        if (pStatus !== wantedStatus && `${pStatus}` !== `${wantedStatus}`) return false;
      }

      return true;
    });
  }, [projects, searchTerm, statusFilter]);

  const loadProjects = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await projectsAPI.getAll();
      setProjects(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.detail || e.message || "Failed to load projects");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadProjects();
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">My Projects</h2>
          <p className="text-gray-600">Projects assigned to you</p>
        </div>

        <button
          onClick={loadProjects}
          className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
          title="Refresh"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search projects..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <select
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="on_hold">On Hold</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
          {error}
        </div>
      )}

      {isLoading ? (
        <LoadingSpinner />
      ) : filteredProjects.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project) => (
            <div key={project.id} className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-gray-800">{project.name}</h3>
                    <p className="text-sm text-gray-600">{project.client}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                    {String(project.status || "—")}
                  </span>
                </div>

                <p className="text-gray-700 text-sm mb-4 line-clamp-2">
                  {project.description || "—"}
                </p>

                <div className="text-sm text-gray-500">
                  <div>Project No: <span className="text-gray-800 font-medium">{project.project_number || "—"}</span></div>
                  <div>Location: <span className="text-gray-800 font-medium">{project.location || "—"}</span></div>
                </div>
              </div>

              <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 flex justify-end">
                <button
                  onClick={() => typeof onSelectProject === "function" && onSelectProject(project)}
                  className="text-orange-600 hover:text-orange-800 text-sm font-medium flex items-center"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  Open
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white p-8 rounded-lg shadow text-center">
          <Building className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-bold text-gray-800 mb-2">No Projects Assigned</h3>
          <p className="text-gray-600">
            You’ll see projects here once an admin assigns you to a project team.
          </p>
        </div>
      )}
    </div>
  );
};

export default MyProjectsView;
